/**
 * Score Query Hooks (Phase 116.6)
 *
 * React Query hooks for fetching score events. Reads directly from
 * `score_event` — the post-cutover source of truth — and returns the
 * `ScoreEventReadModel` shape consumed by the UI.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from './client';
import { useAuth } from './auth';
import type { ScoreEventReadModel } from './edge-functions';
import { fetchInvestorScoreEvents } from './investor-score-events';

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------

export const scoreKeys = {
  all: ['score-events'] as const,
  list: (startupId?: string) =>
    startupId
      ? (['score-events', startupId] as const)
      : (['score-events'] as const),
  history: (startupId: string) => ['score-events', startupId, 'history'] as const,
  /** Investor read path — a separate key so it never shares a cache entry with a founder's full row. */
  investorLatest: (startupId: string) => ['investor-score-events', startupId, 'latest'] as const,
};

// ---------------------------------------------------------------------------
// Internal — map a raw score_event row to the lean read model
// ---------------------------------------------------------------------------

export interface ScoreEventDbRow {
  id: string;
  startup_id: string;
  computed_at: string;
  methodology_version: string;
  composite: ScoreEventReadModel['composite'];
  dimensions: ScoreEventReadModel['dimensions'];
  risks: ScoreEventReadModel['risks'];
  weights_used: { stage?: string; domain?: string } | null;
}

export function rowToReadModel(row: ScoreEventDbRow, startupName: string, startupSector: string | null = null): ScoreEventReadModel {
  return {
    scoreEventId: row.id,
    methodologyVersion: row.methodology_version,
    computedAt: row.computed_at,
    composite: row.composite,
    dimensions: row.dimensions ?? [],
    risks: row.risks ?? [],
    startup: {
      id: row.startup_id,
      name: startupName,
      stage: row.weights_used?.stage ?? null,
      sector: startupSector,
    },
  };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetch all score events for the current user, optionally filtered by startup.
 * RLS limits visibility to events whose startup_id resolves via startup_profiles.user_id.
 */
export function useScoreResults(startupId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: scoreKeys.list(startupId),
    queryFn: async (): Promise<ScoreEventReadModel[]> => {
      if (!isSupabaseConfigured()) return [];

      let query = supabase
        .from('score_event' as never)
        .select('id, startup_id, computed_at, methodology_version, composite, dimensions, risks, weights_used')
        .order('computed_at', { ascending: false });

      if (startupId) {
        query = query.eq('startup_id', startupId);
      }

      const { data, error } = await query;
      if (error) throw error;

      const rows = (data ?? []) as unknown as ScoreEventDbRow[];
      if (rows.length === 0) return [];

      // Hydrate startup names in one batch.
      const ids = Array.from(new Set(rows.map((r) => r.startup_id)));
      const { data: profiles } = await supabase
        .from('startup_profiles')
        .select('id, name, industry')
        .in('id', ids);
      const nameMap = new Map<string, { name: string; sector: string | null }>();
      for (const p of (profiles ?? []) as Array<{ id: string; name: string; industry: string | null }>) {
        nameMap.set(p.id, { name: p.name ?? '', sector: p.industry });
      }

      return rows.map((r) => {
        const profile = nameMap.get(r.startup_id);
        return rowToReadModel(r, profile?.name ?? '', profile?.sector ?? null);
      });
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Convenience hook: fetch the latest score event for a given startup.
 */
export function useLatestScoreResult(startupId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: [...scoreKeys.list(startupId), 'latest'] as const,
    queryFn: async (): Promise<ScoreEventReadModel | null> => {
      if (!isSupabaseConfigured()) return null;

      const { data, error } = await supabase
        .from('score_event' as never)
        .select('id, startup_id, computed_at, methodology_version, composite, dimensions, risks, weights_used')
        .eq('startup_id', startupId)
        .order('computed_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      const row = data as unknown as ScoreEventDbRow;

      const { data: profile } = await supabase
        .from('startup_profiles')
        .select('name, industry')
        .eq('id', row.startup_id)
        .maybeSingle();

      return rowToReadModel(
        row,
        ((profile as { name?: string } | null)?.name) ?? '',
        ((profile as { industry?: string | null } | null)?.industry) ?? null,
      );
    },
    enabled: !!user && !!startupId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Score event history for a startup (trend charting).
 *
 * Phase 116.6: reads from `score_event` (the legacy `score_history` table
 * was orphaned by the 03 cutover and is dropped in this phase's migration).
 * Returns events ordered by computed_at ascending for natural time-series display.
 */
export function useScoreHistory(startupId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: scoreKeys.history(startupId),
    queryFn: async (): Promise<ScoreEventReadModel[]> => {
      if (!isSupabaseConfigured()) return [];

      const { data, error } = await supabase
        .from('score_event' as never)
        .select('id, startup_id, computed_at, methodology_version, composite, dimensions, risks, weights_used')
        .eq('startup_id', startupId)
        .order('computed_at', { ascending: true });

      if (error) throw error;

      const rows = (data ?? []) as unknown as ScoreEventDbRow[];
      if (rows.length === 0) return [];

      const { data: profile } = await supabase
        .from('startup_profiles')
        .select('name, industry')
        .eq('id', startupId)
        .maybeSingle();
      const name = ((profile as { name?: string } | null)?.name) ?? '';
      const sector = ((profile as { industry?: string | null } | null)?.industry) ?? null;

      return rows.map((r) => rowToReadModel(r, name, sector));
    },
    enabled: !!user && !!startupId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Latest score event for a startup AS AN INVESTOR SEES IT — the
 * `investor_score_events` shape (full with a data-room grant; otherwise the
 * score with evidence as backing status). The investor counterpart of
 * `useLatestScoreResult`, which reads the founder's full row and returns
 * nothing to an investor since 20261018130000.
 */
export function useInvestorLatestScore(startupId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: scoreKeys.investorLatest(startupId),
    queryFn: async (): Promise<ScoreEventReadModel | null> => {
      if (!isSupabaseConfigured()) return null;

      const [row] = await fetchInvestorScoreEvents([startupId], { latestOnly: true });
      if (!row) return null;

      const { data: profile } = await supabase
        .from('startup_profiles')
        .select('name, industry')
        .eq('id', row.startup_id)
        .maybeSingle();

      return {
        ...rowToReadModel(
          row,
          ((profile as { name?: string } | null)?.name) ?? '',
          ((profile as { industry?: string | null } | null)?.industry) ?? null,
        ),
        // False → quotes, sources and claims were withheld; the screen offers
        // a data-room request instead.
        rawInputsVisible: row.raw_inputs_visible,
      };
    },
    enabled: !!user && !!startupId,
    staleTime: 5 * 60 * 1000,
  });
}
